// Service for ftching place suggestions using Google Maps Places API
// Usage: import { fetchPlaceSuggestions, resetSessionToken } from './placesAutoCompleteService';

let sessionToken = null;

export async function fetchPlaceSuggestions(input, { countries = ["US"], citiesOnly = true } = {}) {
  if (!window.google || !window.google.maps) {
    throw new Error("Google Maps JS API not loaded");
  }

  // Import all needed classes from places library once
  const { AutocompleteSessionToken, AutocompleteSuggestion } =
    await window.google.maps.importLibrary("places");

  if (!sessionToken) {
    sessionToken = new AutocompleteSessionToken();
  }

  // Convert country codes to lowercase for filtering
  const normalizedCountries = Array.isArray(countries)
    ? countries.map(country => country.toLowerCase())
    : [countries.toLowerCase()];

  const request = {
    input,
    sessionToken,
    language: "en-US",
    origin: null,
    includedPrimaryTypes: citiesOnly ? ["locality"] : ["establishment", "geocode"]
  };

  const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

  return (suggestions || [])
    .map(suggestion => {
      const pred = suggestion.placePrediction;

      const mainText =
        pred.structuredFormat?.mainText?.text || pred.mainText?.text || pred.mainText || "";
      const secondaryText =
        pred.structuredFormat?.secondaryText?.text ||
        pred.secondaryText?.text ||
        pred.secondaryText ||
        "";

      return {
        placeId: pred.placeId || "",
        mainText,
        secondaryText,
        description: pred.description?.text || pred.description || "",
        types: pred.types || []
      };
    })
    .filter(suggestion => {
      const secondaryLower = suggestion.secondaryText.toLowerCase();
      return normalizedCountries.some(
        country => secondaryLower.includes(`, ${country}`) || secondaryLower.endsWith(` ${country}`)
      );
    });
}

export function resetSessionToken() {
  sessionToken = null;
}

export async function fetchPlaceDetails(placeId) {
  if (!window.google || !window.google.maps) {
    throw new Error("Google Maps JS API not loaded");
  }

  const { Place } = await window.google.maps.importLibrary("places");

  const place = new Place({
    id: placeId,
    requestedLanguage: "en"
  });
  await place.fetchFields({
    fields: ["addressComponents", "location", "formattedAddress"]
  });

  const addressComponents = (place.addressComponents || []).map(component => {
    const types = component.types || [];
    const primaryType = types.length > 0 ? types[0] : undefined;

    return {
      type: primaryType,
      shortName: component.shortText,
      shortNameLong: component.text
    };
  });

  let location = null;
  if (place.location) {
    location = {
      lat: place.location.lat(),
      lng: place.location.lng()
    };
  }

  return {
    addressComponents,
    formattedAddress: place.formattedAddress,
    location
  };
}
